import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { CSSProperties, useEffect, useState } from 'react';
import { radius } from '@libertify/ui';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useChatSessionId } from '../../hooks/useChatSessionId';
import { InputPosition, MediaAnalyticsEvents } from '../../types';
import { SiriContainer } from './components/SiriContainer';
import { SendButton } from './components/SendButton';

//  cf https://github.com/bcExpt1123/chatgpt-example/blob/main/frontend/src/components/ChatForm/index.js
export const PromptForm = ({
  onChange,
  onFocus,
  style = {},
  disabled = false,
  gradientBorder = false,
  animatedSubmitButton = false,
  submitButtonLabel,
  value,
  height = 50,
  fontSize,
  inputPosition,
  autoFocus = false,
}: {
  onChange: (prompt: string) => void;
  onFocus?: () => void;
  style?: CSSProperties;
  disabled?: boolean;
  value: string;
  height?: number;
  fontSize?: number;
  gradientBorder?: boolean;
  inputPosition: InputPosition;
  submitButtonLabel?: string;
  animatedSubmitButton?: boolean;
  autoFocus?: boolean;
}) => {
  const { t } = useTranslation('translation', { useSuspense: false });
  const [focused, setFocused] = useState(false);
  const [form] = Form.useForm();
  const prompt = Form.useWatch('prompt', { form, preserve: true });
  const { trackEvent } = useAnalytics();
  const getChatSessionId = useChatSessionId();

  const handleFinish = (values: { prompt: string }) => {
    if (disabled) return;
    trackEvent(MediaAnalyticsEvents.ChatMessageSend, {
      input_position: inputPosition,
      query_source: 'user',
      chat_session_id: getChatSessionId(),
    });
    onChange(values.prompt);
    form.setFieldValue('prompt', '');
  };

  const handleSubmit = () => {
    form.submit();
  };

  const handleFocus = () => {
    trackEvent(MediaAnalyticsEvents.ChatInputFocus, {
      input_position: inputPosition,
      chat_session_id: getChatSessionId(),
    });
    setFocused(true);
    onFocus?.();
  };
  const handleBlur = () => {
    setFocused(false);
  };

  useEffect(() => {
    if (value) form.setFieldValue('prompt', value);
  }, [value, form]);

  return (
    <div style={style}>
      <Form
        className="askme-anything-form"
        form={form}
        onFinish={handleFinish}
        style={{
          position: 'relative',
          borderRadius: radius.pill,
          padding: 3,
          background: gradientBorder
            ? `linear-gradient(58deg, rgba(var(--theme-gradient-input-start),${
                focused ? 1 : 0.5
              }) 0%, rgba(var(--theme-gradient-input-end),${focused ? 1 : 0.5}) 100%)`
            : undefined,
        }}
      >
        <Form.Item
          name="prompt"
          initialValue={value}
          style={{
            background: '#fff',
            borderRadius: radius.pill,
            marginBottom: 0,
          }}
        >
          <Input.TextArea
            autoFocus={autoFocus}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={t('askMeAnything')}
            autoSize={{ minRows: 1, maxRows: 6 }}
            onPressEnter={(e) => {
              // Enter submits; Shift+Enter inserts a newline
              if (!e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            style={{
              minHeight: height,
              resize: 'none',
              borderRadius: radius.pill,
              borderWidth: gradientBorder ? 0 : 2,
              boxShadow:
                gradientBorder && focused
                  ? '0px 0px 30px rgba(var(--theme-gradient-input-start),.5)'
                  : '0px 0px 20px #00000020',
              transition: '1s',
              paddingRight: 60,
              paddingLeft: 20,
              fontSize: fontSize,
              ...(gradientBorder
                ? {
                    background: `linear-gradient(30deg, rgba(var(--theme-gradient-input-start),${
                      focused ? 1 : 0.5
                    }) 0%, rgba(var(--theme-gradient-input-end),${focused ? 1 : 0.5}) 100%) text`,
                    WebkitTextFillColor: 'transparent',
                  }
                : {}),
            }}
          />
        </Form.Item>

        {/* icon button */}
        <Form.Item
          className="askme-anything-icon-button"
          name="sendMsg"
          style={{
            position: 'absolute',
            right: 22,
            top: 'auto',
            bottom: 2,
            transform: 'none',
            marginBottom: 0,
          }}
        >
          {animatedSubmitButton ? (
            <SiriContainer focused={focused} />
          ) : (
            <SendButton
              disabled={!prompt && !disabled}
              busy={disabled}
              gradientBorder={gradientBorder}
              focused={focused}
              height={height}
              handleSubmit={handleSubmit}
              label={submitButtonLabel}
            />
          )}
        </Form.Item>
      </Form>
    </div>
  );
};
